import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';
import { Row, Col, Button } from 'reactstrap';
import { Loader, FileText, Settings, Bell, Database, Users, Shield } from 'react-feather';
import { IUser } from '../../interfaces/user';
import { IApplicationState } from '../../redux/reducers';
import userService from '../../services/userService';
import { routes } from '../routes';

// Components
import FeatureBanner from './components/FeatureBanner';
import VideoCard from './components/VideoCard';
import TutorialCard from './components/TutorialCard';
import DocumentationCard from './components/DocumentationCard';
import GlossarySection from './components/GlossarySection';
import FooterSection from './components/FooterSection';

interface IProps extends RouteComponentProps {
  profile: IUser | undefined;
  getUserProfile: () => Promise<any>;
  setWalkthroughIncomplete: (id: string, complete: boolean) => Promise<any>;
}

const KnowledgeHubV2: React.FC<IProps> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('tutorials');

  useEffect(() => {
    setLoading(true);
    props.getUserProfile().finally(() => {
      setLoading(false);
    });
  }, []);

  const handleStartWalkthrough = () => {
    if (props.profile?.id) {
      setLoading(true);
      props.setWalkthroughIncomplete(props.profile.id, false).finally(() => {
        props.getUserProfile().finally(() => {
          setLoading(false);
          props.history.push(routes.DASHBOARD);
        });
      });
    }
  };

  // Sample data - replace with actual data from API
  const videoTutorials = [
    {
      title: 'Product Dashboard Walkthrough',
      description: 'Complete tour of the product dashboard—from adding products to managing formulations and...',
      category: 'Getting Started',
      duration: '18:24',
      thumbnail: '/placeholder-dashboard.jpg',
      isNew: true
    },
    {
      title: 'AI Recipe Generation Demo',
      description: 'Watch how to go from 905 recipe options to 3 optimized formulations using Journey AI filters an...',
      category: 'AI Features',
      duration: '12:08',
      thumbnail: '/placeholder-recipe.jpg',
      isNew: true
    },
    {
      title: 'Ingredient Search & Analysis',
      description: 'Learn advanced search techniques to find the perfect ingredients for your formulation goals.',
      category: 'Core Features',
      duration: '8:45',
      thumbnail: '/placeholder-search.jpg',
      isNew: false
    }
  ];

  const tutorials = [
    {
      title: 'Getting Started with Journey AI',
      description: 'Learn how to leverage our Operations Scientist™ AI engine for ingredient optimization, nutritional...',
      category: 'Platform',
      duration: '8 min',
      thumbnail: '/placeholder-ai.jpg'
    },
    {
      title: 'Ingredient Database Masterclass',
      description: 'Navigate our comprehensive database of 100,000+ ingredients. Filter by nutrition, cost, sustainability,...',
      category: 'Data',
      duration: '12 min',
      thumbnail: '/placeholder-database.jpg'
    },
    {
      title: 'Sustainability Scoring Deep Dive',
      description: 'Understand how we calculate environmental impact scores and use them to build more...',
      category: 'Sustainability',
      duration: '6 min',
      thumbnail: '/placeholder-sustainability.jpg'
    }
  ];

  const documentationCards = [
    {
      title: 'API Reference',
      description: 'Complete REST API documentation for integrating Journey Foods data into your existing systems. Includes authentication, endpoints, and code examples.',
      icon: <FileText size={24} />,
      badge: 'Updated'
    },
    {
      title: 'Data Integrations',
      description: 'Connect Journey Foods with your ERP, inventory management, and supply chain systems for seamless data flow.',
      icon: <Settings size={24} />,
      badge: 'New'
    },
    {
      title: 'Webhook Configuration',
      description: 'Set up real-time notifications for ingredient price changes, availability alerts, and formulation updates.',
      icon: <Bell size={24} />,
      badge: ''
    },
    {
      title: 'Bulk Data Import',
      description: 'Import your existing product catalog, ingredient lists, and supplier data using our CSV templates.',
      icon: <Database size={24} />,
      badge: ''
    },
    {
      title: 'User Management',
      description: 'Configure team access, roles, and permissions. Set up manufacturer view vs. brand owner permissions.',
      icon: <Users size={24} />,
      badge: ''
    },
    {
      title: 'Compliance & Security',
      description: 'Review our security practices, data handling policies, and regulatory compliance certifications.',
      icon: <Shield size={24} />,
      badge: ''
    }
  ];

  const glossaryItems = [
    { term: 'Journey AI', definition: 'Our proprietary AI engine that powers ingredient insights, formulation optimization, and supply chain decision-making across the platform.' },
    { term: 'Operations Scientist™', definition: "Journey's AI-powered assistant that analyzes ingredients, costs, and sustainability metrics to help you make data-driven decisions faster." },
    { term: 'Product Score', definition: 'A comprehensive rating that combines nutrition, cost efficiency, sustainability, and supply chain reliability into a single actionable metric.' },
    { term: 'Ingredient Database', definition: 'Our searchable database of 100,000+ ingredients with detailed nutritional profiles, sourcing data, and sustainability scores.' },
    { term: 'Sustainability Score', definition: 'Environmental impact rating calculated from carbon footprint, water usage, land use, and supply chain distance for each ingredient.' },
    { term: 'Cost Analysis', definition: 'Real-time pricing data and cost projections to help you optimize ingredient sourcing and maintain target margins.' },
    { term: 'Supply Chain Alerts', definition: 'Automated notifications for price changes, availability issues, or sustainability concerns affecting your watched ingredients.' },
    { term: 'Recipe Optimization', definition: 'AI-driven analysis that suggests ingredient swaps and formulation changes to improve nutrition, reduce costs, or enhance sustainability.' }
  ];

  const blogPosts = [
    {
      title: 'JourneyAI by Journey Foods: Building AI for Human Good',
      author: 'Journey Foods',
      date: 'October 27, 2025',
      url: '#'
    },
    {
      title: 'Digital Tools Can Strengthen Transparency, Traceability, and Trust',
      author: 'Riana Lynn',
      date: 'October 2, 2025',
      url: '#'
    },
    {
      title: 'The Pie: Recession Remix',
      author: 'Riana Lynn',
      date: 'September 25, 2025',
      url: '#'
    }
  ];

  return (
    <div className="knowledge-hub-v2">
      {/* Header */}
      <div className="kh-header">
        <div className="kh-header__content">
          <h1 className="kh-header__title">Knowledge Hub</h1>
          <p className="kh-header__subtitle">Learn how to get the most out of Journey Foods platform</p>
        </div>
        <Button
          disabled={loading}
          onClick={handleStartWalkthrough}
          className="kh-header__tour-btn"
        >
          {loading ? <Loader size={18} className="fa-spin mr-2" /> : null}
          Take a Tour
        </Button>
      </div>

      {/* Feature Banner */}
      <FeatureBanner
        title="Introducing Journey AI 2.0"
        description="Our Operations Scientist™ now generates optimized recipe formulations in seconds. Go from 905 options to 3 perfect matches with AI-powered filtering."
        badge="New Feature"
        image={require('../../assets/images/knowledgehub_banner.png')}
        onTryNow={() => console.log('Try now clicked')}
        onWatchDemo={() => console.log('Watch demo clicked')}
      />

      {/* Navigation Tabs */}
      <div className="kh-tabs">
        <button
          className={`kh-tabs__tab ${activeTab === 'glossary' ? 'kh-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('glossary')}
        >
          Glossary
        </button>
        <button
          className={`kh-tabs__tab ${activeTab === 'guides' ? 'kh-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('guides')}
        >
          Guides
        </button>
        <button
          className={`kh-tabs__tab ${activeTab === 'documentation' ? 'kh-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('documentation')}
        >
          Documentation
        </button>
        <button
          className={`kh-tabs__tab ${activeTab === 'tutorials' ? 'kh-tabs__tab--active' : ''}`}
          onClick={() => setActiveTab('tutorials')}
        >
          Tutorials
        </button>
      </div>

      {/* Video Tutorials Section */}
      {activeTab === 'tutorials' && (
        <div className="kh-section">
          <Row className="kh-video-grid">
            {videoTutorials.map((video, index) => (
              <Col key={index} xs={12} md={6} lg={4} className="kh-video-grid__col">
                <VideoCard {...video} onClick={() => console.log(`Video clicked: ${video.title}`)} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Tutorials Grid */}
      {activeTab === 'guides' && (
        <div className="kh-section">
          <Row className="kh-tutorial-grid">
            {tutorials.map((tutorial, index) => (
              <Col key={index} xs={12} md={6} lg={4} className="kh-tutorial-grid__col">
                <TutorialCard {...tutorial} onClick={() => console.log(`Tutorial clicked: ${tutorial.title}`)} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Documentation Cards */}
      {activeTab === 'documentation' && (
        <div className="kh-section">
          <Row className="kh-doc-grid">
            {documentationCards.map((doc, index) => (
              <Col key={index} xs={12} md={6} lg={4} className="kh-doc-grid__col">
                <DocumentationCard {...doc} onClick={() => console.log(`Doc clicked: ${doc.title}`)} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Glossary Section */}
      {activeTab === 'glossary' && (
        <>
          <div className="kh-section">
            <GlossarySection items={glossaryItems} />
          </div>
          
          {/* Footer Section - Only on Glossary Tab */}
          <FooterSection posts={blogPosts} onViewAll={() => console.log('View all blog posts')} />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: IApplicationState) => ({
  profile: state.user.profile
});

const mapDispatchToProps = {
  getUserProfile: () => userService.getUserProfile(),
  setWalkthroughIncomplete: (id: string, complete: boolean) =>
    userService.setWalkthroughComplete(id, complete)
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(KnowledgeHubV2));
